import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  RewardDistributionHistoryResponse,
  RewardDistributionResult,
} from '../types/reward-distribution.types';

import { getRewardDistributionHistoryService } from 'services/referral-event.service';

import { queryKeys } from 'hooks/queries/query-keys';

const USE_MOCK = false;
const ITEMS_PER_PAGE = 5;

const MOCK_REWARDS: RewardDistributionResult[] = [
  {
    name: 'Refer 10 Friends',
    created_at: '2025-12-15T14:30:00Z',
    prize: { name: '500 RMV Tokens' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'Instantly',
  },
  {
    name: 'Reach Milestone 5',
    created_at: '2025-12-14T10:15:00Z',
    prize: { name: 'Exclusive NFT' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'Periodically (Manual)',
    distribution_link: 'https://etherscan.io/tx/0x1234567890abcdef',
  },
  {
    name: 'Complete Trading Challenge',
    created_at: '2025-12-13T08:45:00Z',
    prize: { name: '100 WoF Spins' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'Periodically (Auto)',
  },
  {
    name: 'Reach Milestone 3',
    created_at: '2025-12-12T16:20:00Z',
    prize: { name: 'Gold Badge NFT' },
    distribution_status: 'Pending',
    reward_distribution_type: 'After Event End Date',
  },
  {
    name: 'Daily Login Streak - 7 Days',
    created_at: '2025-12-11T09:00:00Z',
    prize: { name: '50 RMV Tokens' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'Instantly',
  },
  {
    name: 'First Trade Bonus',
    created_at: '2025-12-10T12:30:00Z',
    prize: { name: '200 RMV Tokens' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'Periodically (Manual)',
    distribution_link: 'https://etherscan.io/tx/0xabcdef1234567890',
  },
  {
    name: 'Refer 5 Friends',
    created_at: '2025-12-09T18:45:00Z',
    prize: { name: '250 RMV Tokens' },
    distribution_status: 'Failed',
    reward_distribution_type: 'Instantly',
  },
  {
    name: 'Community AMA Participation',
    created_at: '2025-12-08T20:00:00Z',
    prize: { name: 'Special Edition NFT' },
    distribution_status: 'Distributed',
    reward_distribution_type: 'After Event End Date',
    distribution_link: 'https://etherscan.io/tx/0x9876543210fedcba',
  },
];

const getMockData = (page: number): RewardDistributionHistoryResponse => {
  const sorted = [...MOCK_REWARDS].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const results = sorted.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const count = sorted.length;

  return {
    count,
    next: startIndex + ITEMS_PER_PAGE < count ? 'next' : null,
    previous: page > 1 ? 'previous' : null,
    results,
  };
};

const fetchRewardDistributionHistory = async (
  eventId: string,
  page: number,
  signal: AbortSignal
): Promise<RewardDistributionHistoryResponse> => {
  if (USE_MOCK) {
    return getMockData(page);
  }
  const response = await getRewardDistributionHistoryService(eventId, signal, page, ITEMS_PER_PAGE);
  return response.data;
};

export const useRewardDistributionHistory = (eventId?: string) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.event.rewardHistory(eventId!, currentPage),
    queryFn: ({ signal }) => fetchRewardDistributionHistory(eventId!, currentPage, signal!),
    enabled: !!eventId,
    retry: false,
  });

  const displayItems = data?.results ?? [];
  const count = data?.count ?? 0;
  const isEmpty = count === 0;
  const showPagination = count > ITEMS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return {
    displayItems,
    isEmpty,
    showPagination,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    isLoading,
  };
};
